import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "../ui/tooltip";
import { formatFullCurrency } from "./projectUtils";
import { api } from "../../lib/api";

const COLUMNS = [
  ["budget", "Presupuesto"],
  ["financing", "Financiación"],
  ["subsidy", "Subvención"],
  ["financial_savings", "Ahorro Fin."],
  ["tax_deduction", "Deducción Fiscal"],
  ["expenses", "Gasto Real"],
  ["income", "Ingresos"],
  ["balance", "Balance"],
];

function Cell({ value, note }) {
  if (value === null || value === undefined) {
    return (
      <TableCell className="text-right font-mono text-muted-foreground/50">
        {note ? (
          <Tooltip>
            <TooltipTrigger asChild><span className="cursor-help">—</span></TooltipTrigger>
            <TooltipContent>{note}</TooltipContent>
          </Tooltip>
        ) : "—"}
      </TableCell>
    );
  }
  return <TableCell className="text-right font-mono">{formatFullCurrency(value)}</TableCell>;
}

export function AnnualBreakdownTable({ token, projectName, projectTotal, compact = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectName) return;
    setLoading(true);
    api.getRdAnnualBreakdown(token, projectName)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token, projectName]);

  if (loading) {
    return <div className="flex items-center justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;
  }
  if (!data || !data.records?.length) {
    return <p className="text-sm text-muted-foreground py-4 px-2">Sin desglose anual disponible en la fuente para este proyecto.</p>;
  }

  const sums = COLUMNS.reduce((acc, [key]) => {
    acc[key] = data.records.reduce((s, r) => (r[key] !== null ? s + r[key] : s), 0);
    return acc;
  }, {});

  return (
    <div data-testid={`annual-breakdown-${projectName.replace(/\s+/g, "-")}`}>
      <TooltipProvider>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Año</TableHead>
                {COLUMNS.map(([key, label]) => (
                  <TableHead key={key} className="text-right whitespace-nowrap">{label}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.records.map((r) => (
                <TableRow key={r.year} data-testid={`annual-row-${projectName.replace(/\s+/g, "-")}-${r.year}`}>
                  <TableCell className="font-mono font-medium">{r.year}</TableCell>
                  {COLUMNS.map(([key]) => (
                    <Cell key={key} value={r[key]} note={r.notes?.[key]} />
                  ))}
                </TableRow>
              ))}
              <TableRow className="bg-muted/40 font-semibold">
                <TableCell>Suma años</TableCell>
                {COLUMNS.map(([key]) => (
                  <TableCell key={key} className="text-right font-mono">{formatFullCurrency(sums[key])}</TableCell>
                ))}
              </TableRow>
              {projectTotal && (
                <TableRow className="bg-primary/5 font-semibold">
                  <TableCell>Total oficial proyecto</TableCell>
                  {COLUMNS.map(([key]) => (
                    <TableCell key={key} className="text-right font-mono">
                      {projectTotal[key] !== undefined ? formatFullCurrency(projectTotal[key]) : "—"}
                    </TableCell>
                  ))}
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TooltipProvider>
      {!compact && (
        <p className="text-xs text-muted-foreground mt-3 px-1">
          Fuente: {data.source_filename} · {data.source_sheet} · hash {data.source_sha256?.slice(0, 16)}… ·
          Las celdas "—" son valores no informados en el libro origen ("-", "/", o error de fórmula) — nunca se han convertido a cero.
        </p>
      )}
    </div>
  );
}
